import { Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { clampCountdownSeconds, countdownRemainingSeconds, DEFAULT_COUNTDOWN_SECONDS } from '../../../utils/countdownTimerCodeUtils';
import { useProgramElapsedSeconds } from '../../../hooks/dashboard/useProgramElapsedSeconds';
import { formatElapsedDurationSeconds } from '../../../utils/formatElapsedDuration';
import { CHART_RED } from '../../../theme/theme-colors';
import { useDashboardCapabilities } from '../dashboardCapabilities';
import { useWidgetHeader } from './WidgetHeaderContext';
import { usePersistDashboardWidgetContent } from './usePersistDashboardWidgetContent';
import { TimerDurationDisplay } from './TimerDurationDisplay';
import CountdownDurationHeaderField from './CountdownDurationHeaderField';

interface CountdownTimerWidgetProps {
  id: string;
  countdownSeconds?: number;
}

const CountdownTimerWidget = ({ id, countdownSeconds }: CountdownTimerWidgetProps) => {
  const { t } = useTranslation();
  const programElapsedSeconds = useProgramElapsedSeconds();
  const { canEditWidgetDataSelection } = useDashboardCapabilities();
  const { persistWidgetContent } = usePersistDashboardWidgetContent(id);

  const configuredSeconds = clampCountdownSeconds(countdownSeconds ?? DEFAULT_COUNTDOWN_SECONDS);
  const remainingSeconds = countdownRemainingSeconds(countdownSeconds ?? DEFAULT_COUNTDOWN_SECONDS, programElapsedSeconds);

  const headerTitle = useMemo(() => t('CountdownTimerWidget.title', 'Countdown'), [t]);
  const time = useMemo(() => formatElapsedDurationSeconds(remainingSeconds), [remainingSeconds]);
  const durationDisabled = !canEditWidgetDataSelection;

  const handleDurationCommit = useCallback(
    (next: number) => {
      persistWidgetContent({ countdownSeconds: next });
    },
    [persistWidgetContent]
  );

  const toolbarEnd = useMemo(
    () => (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
        <CountdownDurationHeaderField seconds={configuredSeconds} disabled={durationDisabled} onCommit={handleDurationCommit} />
      </Stack>
    ),
    [configuredSeconds, durationDisabled, handleDurationCommit]
  );

  useWidgetHeader({ title: headerTitle, toolbarEnd }, [headerTitle, toolbarEnd]);

  return (
    <TimerDurationDisplay
      time={time}
      color={CHART_RED}
      ariaLabel={t('CountdownTimerWidget.aria.remaining', { defaultValue: 'Countdown remaining {{time}}', time })}
    />
  );
};

export default CountdownTimerWidget;
